import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, Phone, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import ShareRow from "@/components/ShareRow";
import { Reveal, MaskLine } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 rounded-sm border-white/15 bg-coal px-4 text-ivory placeholder:text-white/30 focus-visible:ring-gold";

const Contact = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message received. We will respond shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        path="/contact"
        description="Contact Smart Nagar Nigam — contact@smartnagarnigam.com · +91 92205 79429 · Office 02, Ground Floor, A-25, ARV Park, M1, Sector 63, Noida, Uttar Pradesh 201301."
      />
      <section className="relative overflow-hidden pt-28 md:pt-52" data-testid="contact-hero">
        <div className="mx-auto max-w-[1600px] px-5 pb-16 md:px-10 md:pb-24">
          <MaskLine delay={0.1}>
            <span className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-gold md:text-sm">Contact</span>
          </MaskLine>
          <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-ivory">
            <MaskLine delay={0.25} className="text-[11vw] md:text-[6.5vw]">Start the</MaskLine>
            <MaskLine delay={0.4} className="text-[11vw] md:text-[6.5vw]">
              <span className="text-outline">Conversation.</span>
            </MaskLine>
          </h1>
        </div>
      </section>

      <section className="border-t border-white/10" data-testid="contact-section">
        <div className="mx-auto grid max-w-[1600px] gap-16 px-5 py-16 md:px-10 md:py-24 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-px border border-white/10 bg-white/10">
              {[
                { icon: Mail, k: "Email", v: CONTACT.email, href: `mailto:${CONTACT.email}`, tid: "contact-email" },
                { icon: Phone, k: "Phone", v: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, tid: "contact-phone" },
                { icon: MapPin, k: "Office", v: CONTACT.address, href: null, tid: "contact-address" },
              ].map((c) => (
                <div key={c.k} className="bg-obsidian p-8">
                  <c.icon size={20} className="text-gold" aria-hidden="true" />
                  <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-white/40">{c.k}</p>
                  {c.href ? (
                    <a href={c.href} data-testid={c.tid} className="mt-2 block font-display text-lg font-bold leading-snug text-ivory transition-colors duration-300 hover:text-gold">
                      {c.v}
                    </a>
                  ) : (
                    <p data-testid={c.tid} className="mt-2 font-display text-lg font-bold leading-snug text-ivory">{c.v}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-10">
              <ShareRow title="Contact Smart Nagar Nigam" path="/contact" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            {done ? (
              <div className="border border-gold/40 bg-coal p-10 md:p-14" data-testid="contact-success">
                <p className="text-xs uppercase tracking-[0.3em] text-gold">Message Received</p>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ivory md:text-4xl">
                  Thank you, {form.full_name.split(" ")[0]}.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-white/65">
                  Your message has been received and a confirmation is on its way to your
                  inbox. The Smart Nagar Nigam team will respond shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-white/10 bg-coal p-8 md:p-12" data-testid="contact-form">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="c_name" className="text-xs uppercase tracking-[0.2em] text-white/50">Full Name *</Label>
                    <Input id="c_name" data-testid="contact-name-input" required minLength={2} value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c_email" className="text-xs uppercase tracking-[0.2em] text-white/50">Email *</Label>
                    <Input id="c_email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c_phone" className="text-xs uppercase tracking-[0.2em] text-white/50">Phone</Label>
                    <Input id="c_phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c_subject" className="text-xs uppercase tracking-[0.2em] text-white/50">Subject *</Label>
                    <Input id="c_subject" data-testid="contact-subject-input" required minLength={2} value={form.subject} onChange={set("subject")} placeholder="What is this about?" className={inputCls} />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="c_message" className="text-xs uppercase tracking-[0.2em] text-white/50">Message *</Label>
                    <Textarea id="c_message" data-testid="contact-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Write your message…" className="rounded-sm border-white/15 bg-coal px-4 text-ivory placeholder:text-white/30 focus-visible:ring-gold" />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="mt-8 inline-flex items-center gap-3 bg-gold px-10 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {loading ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
